/**
 * Key Code Mapping
 * 
 * Maps human-readable key names to JavaScript key codes for input filtering.
 * Used by filter parsers to convert comma-separated key strings into filter objects.
 * 
 * @example
 * KEY_CODE_MAP['A'] // 65
 * KEY_CODE_MAP['ESC'] // 27
 * KEY_CODE_MAP['ENTER'] // 13
 */

export const KEY_CODE_MAP: Record<string, number> = {
  // Letters
  'A': 65, 'B': 66, 'C': 67, 'D': 68, 'E': 69, 'F': 70, 'G': 71, 'H': 72,
  'I': 73, 'J': 74, 'K': 75, 'L': 76, 'M': 77, 'N': 78, 'O': 79, 'P': 80,
  'Q': 81, 'R': 82, 'S': 83, 'T': 84, 'U': 85, 'V': 86, 'W': 87, 'X': 88,
  'Y': 89, 'Z': 90,

  // Numbers
  '0': 48, '1': 49, '2': 50, '3': 51, '4': 52,
  '5': 53, '6': 54, '7': 55, '8': 56, '9': 57,

  // Function Keys
  'F1': 112, 'F2': 113, 'F3': 114, 'F4': 115, 'F5': 116, 'F6': 117,
  'F7': 118, 'F8': 119, 'F9': 120, 'F10': 121, 'F11': 122, 'F12': 123,

  // Special Keys
  'BACKSPACE': 8,
  'TAB': 9,
  'ENTER': 13,
  'RETURN': 13, // Alias for ENTER
  'SHIFT': 16,
  'CTRL': 17,
  'CONTROL': 17, // Alias for CTRL
  'ALT': 18,
  'PAUSE': 19,
  'CAPSLOCK': 20,
  'ESC': 27,
  'ESCAPE': 27, // Alias for ESC
  'SPACE': 32,
  'PAGEUP': 33,
  'PAGEDOWN': 34,
  'END': 35,
  'HOME': 36,

  // Arrow Keys
  'LEFT': 37,
  'UP': 38,
  'RIGHT': 39,
  'DOWN': 40,
  'ARROWLEFT': 37, // Alias
  'ARROWUP': 38, // Alias
  'ARROWRIGHT': 39, // Alias
  'ARROWDOWN': 40, // Alias

  // More Special Keys
  'INSERT': 45,
  'DELETE': 46,
  'META': 91, // Windows/Command key
  'WINDOWS': 91, // Alias
  'COMMAND': 91, // Alias
  'SELECT': 93,
  'CONTEXTMENU': 93,

  // Numpad
  'NUMPAD0': 96, 'NUMPAD1': 97, 'NUMPAD2': 98, 'NUMPAD3': 99,
  'NUMPAD4': 100, 'NUMPAD5': 101, 'NUMPAD6': 102, 'NUMPAD7': 103,
  'NUMPAD8': 104, 'NUMPAD9': 105,
  'MULTIPLY': 106,
  'ADD': 107,
  'SUBTRACT': 109,
  'DECIMAL': 110,
  'DIVIDE': 111,

  // Locks
  'NUMLOCK': 144,
  'SCROLLLOCK': 145,

  // Punctuation and Symbols (some may vary by keyboard layout)
  'SEMICOLON': 186,
  'EQUALS': 187,
  'COMMA': 188,
  'MINUS': 189,
  'PERIOD': 190,
  'SLASH': 191,
  'BACKQUOTE': 192,
  'TILDE': 192, // Alias
  'OPENBRACKET': 219,
  'BACKSLASH': 220,
  'CLOSEBRACKET': 221,
  'QUOTE': 222,
};

/**
 * Reverse map: key code to name (for display purposes)
 */
export const KEY_NAME_MAP: Record<number, string> = Object.entries(KEY_CODE_MAP).reduce(
  (acc, [name, code]) => {
    // Only store the first name for each code (prefer shorter names)
    if (!acc[code] || name.length < acc[code].length) {
      acc[code] = name;
    }
    return acc;
  },
  {} as Record<number, string>
);

type ParsedFilter = { key: string; modifiers: string[] };

export const MOD_MAP: Record<string, string> = {
  CTRL: "MOD_CONTROL",
  ALT: "MOD_ALT",
  SHIFT: "MOD_SHIFT",
  META: "MOD_WIN",
  WIN: "MOD_WIN",
};

export const CANON_MOD_ORDER = ["CTRL", "SHIFT", "ALT", "META"] as const;

function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

export function buildAllCombos(includeNone: boolean): string[] {
  const canon = CANON_MOD_ORDER.map(k => MOD_MAP[k]);
  const results: string[] = [];

  if (includeNone) results.push(""); // no modifiers

  const n = canon.length;
  for (let mask = 1; mask < (1 << n); mask++) {
    const parts: string[] = [];
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) parts.push(canon[i]);
    }
    results.push(parts.join("-"));
  }

  return results;
}

export function parseKeyString(input: string): ParsedFilter {
  const trimmed = input.trim();

  // Extract key and modifier block
  const match = trimmed.match(/^([^\[\]]+)\[([^\[\]]*)\]$/);

  if (!match) {
    // No modifiers specified → NONE
    return { key: trimmed.toUpperCase(), modifiers: [""] };
  }

  const key = match[1].trim().toUpperCase();
  const inside = match[2].trim().toUpperCase();

  // NONE or empty
  if (!inside || inside === "NONE") {
    return { key, modifiers: [""] };
  }

  // ALL → every permutation (including none)
  if (inside === "ALL") {
    return { key, modifiers: buildAllCombos(true) };
  }

  // Split OR-combos by pipe
  const combos = inside
    .split("|")
    .map(c => c.trim())
    .filter(Boolean)
    .map(combo => {
      // Split AND-modifiers by +
      const tokens = combo
        .split("+")
        .map(t => t.trim())
        .filter(Boolean);

      const mapped = tokens
        .map(t => MOD_MAP[t])
        .filter(Boolean);

      // Canonical ordering (CTRL → SHIFT → ALT → WIN)
      const ordered = unique(
        CANON_MOD_ORDER
          .map(k => MOD_MAP[k])
          .filter(m => mapped.includes(m))
      );

      return ordered.join("-"); // what C++ expects
    });

  return { key, modifiers: combos.length ? combos : [""] };
}

/**
 * Check if a key name is valid
 */
export function isValidKeyName(keyName: string): boolean {
  return KEY_CODE_MAP[keyName.toUpperCase()] !== undefined;
}

/**
 * Get key code from key name (case insensitive)
 */
export function getKeyCode(keyName: string): number | undefined {
  return KEY_CODE_MAP[keyName.toUpperCase()];
}

